import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { getCookie } from '~shared/utils/cookie.util';
import { AccountInfoService } from '../../core/modules/identity/services/account-info.service';
import {
  CUSTOMER_EVENTS_URL_CRM,
  CUSTOMER_NEWSLETTER_CONSENT_URL_CRM,
  EXPONEA_COOKIE,
  CUSTOMER_VISITORS_EVENT_URL_CRM,
} from '~shared/constants';
import { map, mergeMap, take } from 'rxjs/operators';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { Observable } from 'rxjs';
import { QueryParamsEncoder } from '~shared/helpers/query-encoder';

export type ConsentType = 'email_marketing' | 'legitimate_interest' | 'phone_communication';

// SERVICE EXCLUDED FROM SONAR
@Injectable({
  providedIn: 'root',
})
export class ExponeaService {
  constructor(
    private accountInfoService: AccountInfoService,
    private queryParamsEncoder: QueryParamsEncoder,
    private httpClient: HttpClient,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {}

  public sendNewsletterConsentData(email: string, source: string, pageSlug = ''): Observable<void | Object> {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const exponeaCookie = getCookie(EXPONEA_COOKIE);

    return this.accountInfoService.getAccountInfo().pipe(
      mergeMap((accountInfo) =>
        this.httpClient.post(
          CUSTOMER_NEWSLETTER_CONSENT_URL_CRM(),
          JSON.stringify({
            email,
            hardId: accountInfo?.id || '',
            softId: exponeaCookie,
            market,
            type: 'email_marketing',
            source,
            pageSlug,
          })
        )
      ),
      take(1)
    );
  }

  public sendPhoneConsentData(
    source: string,
    pageSlug = window.location.pathname.split('/').pop() || '',
    phone?: string
  ): Observable<void | Object> {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const exponeaCookie = getCookie(EXPONEA_COOKIE);

    return this.accountInfoService.getAccountInfo().pipe(
      mergeMap((accountInfo) =>
        this.httpClient.post(
          CUSTOMER_NEWSLETTER_CONSENT_URL_CRM(),
          JSON.stringify({
            email: accountInfo.email,
            hardId: accountInfo?.id || '',
            softId: exponeaCookie,
            market,
            type: 'phone_communication',
            source,
            pageSlug,
            ...(phone ? { phone } : {})
          })
        )
      ),
      take(1)
    );
  }

  public sendNewCustomerData(type: 'registration' | 'login', email?: string): void {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const exponeaCookie = getCookie(EXPONEA_COOKIE);

    this.accountInfoService
      .getAccountInfo()
      .pipe(
        mergeMap((accountInfo) =>
          this.httpClient.post(CUSTOMER_EVENTS_URL_CRM(), {
            email: email || accountInfo?.email || '',
            hardId: accountInfo?.id || '',
            market,
            softId: exponeaCookie,
            type,
          })
        ),
        take(1)
      )
      .subscribe();
  }

  public sendVisitorsEventData(email: string, source: string, slug = '', param = ''): Observable<object> {
    const market = getCountryDomainCode(this.webAppBuyerRequest);

    return this.accountInfoService.getAccountInfo().pipe(
      mergeMap(() =>
        this.httpClient.post(
          CUSTOMER_VISITORS_EVENT_URL_CRM(),
          JSON.stringify({
            email,
            market,
            type: 'lottery_sign_in',
            params: {
              slug,
              source,
              extra_param: param,
            },
          })
        )
      ),
      take(1)
    );
  }

  public getIsConsentActive(type: ConsentType): Observable<boolean> {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    const exponeaCookie = getCookie(EXPONEA_COOKIE);

    return this.accountInfoService.getAccountInfo().pipe(
      mergeMap((accountInfo) =>
        this.httpClient.get<{ is_active: boolean }>(CUSTOMER_NEWSLETTER_CONSENT_URL_CRM(), {
          params: new HttpParams({
            fromObject: {
              market,
              email: accountInfo.email,
              hardId: accountInfo.id,
              softId: exponeaCookie,
              type,
            },
            encoder: this.queryParamsEncoder,
          }),
        })
      ),
      take(1),
      map((res) => res?.is_active ?? false)
    );
  }
}
